<template>
  <div class="dialog">
    <span class="cls"></span>
    <div class="paper">
      <div class="header">
        <h2>{{ tutorialInfo.title || '튜토리얼' }}</h2>
        <span @click="close">
          <i class="ph ph-x"></i>
        </span>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="loading-state">
        <i class="ph ph-spinner"></i>
        <p>불러오는 중...</p>
      </div>

      <!-- 슬라이드 -->
      <template v-else-if="slides.length > 0">
        <div class="slides-container">
          <transition :name="slideDirection" mode="out-in">
            <div class="slide" :key="currentSlide">
              <div class="slide-content">
                <!-- 이미지/GIF -->
                <div class="slide-media" v-if="currentSlideData.image_url">
                  <img :src="currentSlideData.image_url" :alt="currentSlideData.title" />
                </div>
                <div class="slide-media placeholder" v-else>
                  <i :class="currentSlideData.icon || 'ph-fill ph-info'"></i>
                </div>

                <!-- 텍스트 -->
                <div class="slide-text">
                  <h3>{{ currentSlideData.title }}</h3>
                  <p v-html="currentSlideData.content"></p>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- 하단 네비게이션 -->
        <div class="nav-area">
          <div class="dots">
            <span 
              v-for="(_, idx) in slides.length" 
              :key="idx" 
              :class="{ active: currentSlide === idx }"
              @click="goToSlide(idx)"
            ></span>
          </div>

          <div class="nav-buttons">
            <button v-if="currentSlide > 0" class="nav-btn prev" @click="prev">
              <i class="ph ph-arrow-left"></i>
              이전
            </button>
            <button v-if="currentSlide < slides.length - 1" class="nav-btn next" @click="next">
              다음
              <i class="ph ph-arrow-right"></i>
            </button>
            <button v-else class="nav-btn complete" @click="complete">
              완료
              <i class="ph ph-check"></i>
            </button>
          </div>
        </div>
      </template>

      <!-- 데이터 없음 -->
      <div v-else class="empty-state">
        <i class="ph ph-warning"></i>
        <p>튜토리얼 데이터가 없습니다.</p>
        <button @click="close">닫기</button>
      </div>

      <!-- 건너뛰기 -->
      <button class="skip-btn" @click="close" v-if="!loading && slides.length > 0">
        건너뛰기
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, inject, onMounted } from 'vue'

export default {
  name: 'TutorialDialog',
  props: {
    oth_showTutorial: Function,
    type: {
      type: String,
      default: 'welcome'  // welcome, customer, crm, calendar 등
    },
    // 첫 접속 체크용 (welcome 타입일 때만)
    markComplete: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')

    const loading = ref(true)
    const tutorialInfo = ref({})
    const slides = ref([])
    const currentSlide = ref(0)
    const slideDirection = ref('slide-left')

    // 현재 슬라이드 데이터
    const currentSlideData = computed(() => {
      return slides.value[currentSlide.value] || {}
    })

    // 튜토리얼 데이터 로드
    const loadTutorial = async () => {
      loading.value = true
      try {
        // 튜토리얼 정보 가져오기
        const infoResult = await sql_query(`
          SELECT * FROM tutorial_info 
          WHERE type = '${props.type}' AND is_active = 1
          LIMIT 1
        `)
        
        if (infoResult && infoResult.length > 0) {
          tutorialInfo.value = infoResult[0]
        }

        // 슬라이드 데이터 가져오기
        const slidesResult = await sql_query(`
          SELECT * FROM tutorial_slides 
          WHERE type = '${props.type}' AND is_active = 1
          ORDER BY slide_order ASC
        `)
        
        slides.value = slidesResult || []
      } catch (e) {
        console.error('튜토리얼 로드 실패:', e)
        slides.value = []
      } finally {
        loading.value = false
      }
    }

    // 네비게이션
    const next = () => {
      if (currentSlide.value < slides.value.length - 1) {
        slideDirection.value = 'slide-left'
        currentSlide.value++
      }
    }

    const prev = () => {
      if (currentSlide.value > 0) {
        slideDirection.value = 'slide-right'
        currentSlide.value--
      }
    }

    const goToSlide = (idx) => {
      slideDirection.value = idx > currentSlide.value ? 'slide-left' : 'slide-right'
      currentSlide.value = idx
    }

    // 완료
    const complete = async () => {
      // markComplete가 true면 DB에 완료 기록 (첫 접속 welcome용)
      if (props.markComplete && props.type === 'welcome') {
        try {
          const existing = await sql_query(`
            SELECT idx FROM user_settings WHERE mb_id = '${member_info.mb_id}'
          `)
          
          if (existing && existing.length > 0) {
            await sql_query(`
              UPDATE user_settings 
              SET tutorial_done = 1 
              WHERE mb_id = '${member_info.mb_id}'
            `)
          } else {
            await sql_query(`
              INSERT INTO user_settings (mb_id, tutorial_done) 
              VALUES ('${member_info.mb_id}', 1)
            `)
          }
        } catch (e) {
          console.error('튜토리얼 완료 저장 실패:', e)
        }
      }

      props.oth_showTutorial(false)
    }

    const close = () => {
      props.oth_showTutorial(false)
    }

    onMounted(() => {
      loadTutorial()
    })

    return {
      loading,
      tutorialInfo,
      slides,
      currentSlide,
      currentSlideData,
      slideDirection,
      next,
      prev,
      goToSlide,
      complete,
      close
    }
  }
}
</script>

<style scoped>
.dialog {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: var(--z-index-top);
  backdrop-filter: blur(10px);
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  position: relative;
  width: 90%;
  max-width: 520px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.paper > .header {
  width: 100%;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
  box-sizing: border-box;
}

.paper > .header > h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
}

.paper > .header > span {
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s;
}

.paper > .header > span:hover {
  background: var(--color-background-light);
}

.paper > .header > span > i {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

/* Loading */
.loading-state {
  padding: 80px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-state > i {
  font-size: 48px;
  color: var(--color-brand);
  animation: spin 1s linear infinite;
}

.loading-state > p {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 슬라이드 컨테이너 */
.slides-container {
  min-height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}

.slide {
  width: 100%;
}

.slide-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
}

/* 슬라이드 미디어 */
.slide-media {
  width: 100%;
  max-width: 400px;
  aspect-ratio: 16 / 10;
  border-radius: 12px;
  overflow: hidden;
  background: var(--color-background-light);
}

.slide-media > img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.slide-media.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-media.placeholder > i {
  font-size: 64px;
  color: var(--color-brand);
  opacity: 0.7;
}

/* 슬라이드 텍스트 */
.slide-text {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.slide-text > h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.slide-text > p {
  font-size: 15px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

/* 하단 네비게이션 */
.nav-area {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 32px;
  border-top: 1px solid var(--color-background-light);
}

.dots {
  display: flex;
  gap: 8px;
}

.dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-background-light);
  cursor: pointer;
  transition: all 0.2s;
}

.dots span.active {
  background: var(--color-brand);
  width: 24px;
  border-radius: 4px;
}

.nav-buttons {
  display: flex;
  gap: 8px;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-btn.prev {
  background: var(--color-background-light);
  color: var(--color-text-secondary);
}

.nav-btn.next,
.nav-btn.complete {
  background: var(--color-brand);
  color: #fff;
}

/* 건너뛰기 버튼 */
.skip-btn {
  position: absolute;
  top: 16px;
  right: 56px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  font-size: 14px;
  color: var(--color-text-third);
  cursor: pointer;
  transition: color 0.2s;
}

.skip-btn:hover {
  color: var(--color-text-secondary);
}

/* Empty state */
.empty-state {
  padding: 60px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.empty-state > i {
  font-size: 48px;
  color: var(--color-text-third);
}

.empty-state > p {
  font-size: 14px;
  color: var(--color-text-third);
  margin: 0;
}

.empty-state > button {
  padding: 10px 24px;
  background: var(--color-brand);
  border: none;
  border-radius: 8px;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
}

/* 슬라이드 애니메이션 */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease;
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* 모바일 대응 */
@media (max-width: 480px) {
  .dialog > .paper {
    width: 95%;
    border-radius: 16px;
  }

  .slides-container {
    min-height: 300px;
    padding: 24px 20px;
  }

  .nav-area {
    padding: 16px 20px;
  }
}
</style>